import { useForm } from 'react-hook-form'
import { Button, Dropdown } from 'components/common'
import Modal from 'components/modal'
import { yupResolver } from '@hookform/resolvers/yup'
import { fullRmaSchema } from 'utils/schema/oderSchema'
import { useEffect, useState } from 'react'
import { InputOptions } from 'types/input'
import { getRmaConfigurations } from 'utils/api/configApi'
import toast from 'react-hot-toast'
import { createFullRma } from 'utils/api/orderApi'

type CreateRmaModalProps = {
  isOpen: boolean
  closeModal: () => void
  onSuccess: () => void
  orderNo: string
  orderId: string
}

interface Rma {
  refundType: string
  reason: string
  condition: string
}

const CreateRmaModal = ({
  isOpen,
  closeModal,
  orderNo,
  orderId,
  onSuccess,
}: CreateRmaModalProps) => {
  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Rma>({
    resolver: yupResolver(fullRmaSchema),
    defaultValues: {
      reason: '',
      condition: '',
      refundType: 'DEFAULT',
    },
  })

  const [conditions, setConditions] = useState<InputOptions[]>([])
  const [reasons, setReasons] = useState<InputOptions[]>([])

  const [loading, setLoading] = useState<boolean>(false)

  const handleFullRma = async (data: Rma) => {
    setLoading(true)

    const toastId = toast.loading('Generating Rma..')

    try {
      await createFullRma(orderId, data)
      onSuccess()
      toast.success('RMA Generated Successfully', { id: toastId })
      closeModal()
    } catch {
      toast.error('Failed to Generate RMA', { id: toastId })
    } finally {
      setLoading(false)
    }
  }

  const getReasonsAndConditions = async () => {
    try {
      const { data } = await getRmaConfigurations()

      if (data?.rmaConditions?.length > 0) {
        const rmaConditions = data.rmaConditions.map(
          (condition: string): InputOptions => ({
            label: condition,
            value: condition,
            disabled: false,
          }),
        )

        rmaConditions.unshift({
          label: '--SELECT--',
          value: '',
          disabled: false,
        })

        setConditions(rmaConditions)
      }

      if (data?.rmaReasons?.length > 0) {
        const rmaReasons = data?.rmaReasons.map(
          (reason: string): InputOptions => ({
            label: reason,
            value: reason,
            disabled: false,
          }),
        )

        rmaReasons.unshift({
          label: '--SELECT--',
          value: '',
          disabled: false,
        })

        setReasons(rmaReasons)
      }
    } catch (error) {
      setConditions([])
      setReasons([])
    }
  }

  useEffect(() => {
    getReasonsAndConditions()
  }, [])

  return (
    <Modal
      width={500}
      title={`Generate RMA for Order #${orderNo}`}
      titleClassname="text-xl font-semibold"
      isOpen={isOpen}
      closeModal={closeModal}
      customStyle={{
        padding: '25px 30px',
      }}
    >
      <form onSubmit={handleSubmit(handleFullRma)}>
        {/* Return Reason */}
        <Dropdown
          options={reasons}
          defaultValue={reasons[0]?.value as string}
          placeholder="Select a reason"
          name="reason"
          setValue={setValue}
          label="Return Reason"
          error={errors?.reason?.message}
        />

        {/* Condition */}
        <Dropdown
          label="Condition"
          options={conditions}
          defaultValue={conditions[0]?.value as string}
          placeholder="Select the condition"
          name="condition"
          setValue={setValue}
          parentClassName="mt-3"
          error={errors?.condition?.message}
        />

        {/* Refund Type */}
        <div className="mt-2">
          {/* <span className="text-xs font-semibold text-sidebar-primary">
            Refund Type
          </span>

          <div>
            {RMA_REFUND_TYPES.map(({ label, value }) => (
              <label className="flex items-center gap-2" key={value}>
                <input
                  type="radio"
                  value={value}
                  {...register('refundType', { required: true })}
                />
                {label}
              </label>
            ))}
          </div> */}
        </div>

        {/* Actions */}
        <div className="mt-10 flex w-full max-w-sm items-center gap-2">
          <Button
            disabled={loading}
            type="submit"
            className="w-full"
            buttonText="Confirm"
          />
          <Button
            className="w-full"
            buttonText="Cancel"
            variant="outline"
            type="button"
            onClick={closeModal}
          />
        </div>
      </form>
    </Modal>
  )
}

export default CreateRmaModal
